import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Tabs,
  Tab,
  Box,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  EditExperienceList,
  getExperienceData,
} from "../../Services/ExperienceApi";
import { toast, ToastContainer } from "react-toastify";
import { ReactComponent as ArrowbackIcon } from "../../Assets/arrowback.svg";
import { ReactComponent as SettingIcon } from "../../Assets/setting.svg";
import { ReactComponent as EditIconIcon } from "../../Assets/EditIcon.svg";
import { ReactComponent as ExperienceIcon } from "../../Assets/ExperienceIcon.svg";
import { ReactComponent as TouchpointIcon } from "../../Assets/touchpointsIcon.svg";

import {
  ReactFlow,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  Edge,
  Node,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import "@xyflow/react/dist/style.css";
import ColorSelectorNode from "../Experience/RotateNode";
import AddButtonNode from "../Experience/AddButtonNode";
import { ReactFlowProvider } from "reactflow";
import Tooltip from "@mui/material/Tooltip";

const snapGrid: [number, number] = [20, 20];

const nodeTypes = {
  selectorNode: ColorSelectorNode,
  addButtonNode: AddButtonNode,
};

const defaultViewport = { x: 0, y: 50, zoom: 1 };

const EditExperience = () => {
  const [activeTab, setActiveTab] = useState(0);
  const location = useLocation();
  const [errors, setErrors] = useState<any>({ name: false, type: false });
  const navigate = useNavigate();
  const [nodes, setNodes, onNodesChange] = useNodesState<Node<any>>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge<any>>([]);
  const { id: experienceId } = useParams();
  const [reactFlowInstance, setReactFlowInstance] = useState<any>(null);
  const flowWrapperRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [isloading, setIsLoading] = useState(false);
  const [name, setName] = useState(false);
  const onConnect = useCallback(
    (params: Connection) =>
      setEdges((eds: any) => addEdge({ ...params, animated: true }, eds)),
    [setEdges]
  );
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    if (newValue === 0) {
      navigate(`/edit-experience/${experienceId}/setting`);
    } else {
      navigate(`/edit-experience/${experienceId}`);
    }
  };

  useEffect(() => {
    if (location.pathname.includes("setting")) {
      setActiveTab(0);
    } else {
      setActiveTab(1);
    }
  }, [location.pathname]);

  useEffect(() => {
    const fetchExperience = async () => {
      if (!experienceId) return;

      try {
        setIsLoading(true);
        const response = await getExperienceData(experienceId);
        const data = response.data;

        setFormData({
          name: data.name || "",
          type: data.type || "",
          description: data.description || "",
        });
        setName(data.name);
        sessionStorage.setItem("nodeType", data.type);
        const newNodes: any = [];
        const newEdges: any[] = [];

        let currentY = 100;

        let pageNode: any;
        sessionStorage.setItem(
          "pendingNodeAddition",
          JSON.stringify({
            experinceId: experienceId,
          })
        );

        let childrenCount = 0;
        if (data.experience_page_setting) {
          const pageId = `pageId_${data.experience_page_setting.id}`;

          sessionStorage.setItem(
            "pendingNodeAddition",
            JSON.stringify({
              experinceId: experienceId,
              pageId: pageId,
            })
          );

          pageNode = {
            id: pageId,
            type: "selectorNode",
            position: { x: 0, y: 100 },
            draggable: false,
            data: {
              ...data.experience_page_setting,
              label: "Pages",
              type: "page",
              id: pageId,
              title: data.experience_page_setting.title,
              matchType: data.experience_page_setting.match_type,
            },
          };
        } else {
          const pageId = `pageId_default`;
          pageNode = {
            id: pageId,
            type: "selectorNode",
            position: { x: 0, y: 100 },
            draggable: false,
            data: {
              label: "Pages",
              type: "page",
              id: pageId,
            },
          };
        }
        newNodes.push(pageNode);

        const segments = data.experience_segments || [];
        let isMeterValue = false;
        if (segments.length > 0) {
          segments.forEach((segment: any, index: number) => {
            if (
              (segment?.pageview_meters &&
                Array.isArray(segment?.pageview_meters?.meter_values) &&
                segment?.pageview_meters?.meter_values.length > 0) ||
              segment?.viewport_exit_settings?.id ||
              (Array.isArray(segment.experience_cookie_setting) &&
                segment.experience_cookie_setting.length > 0) ||
              segment.experience_template || segment.interactions ||
              segment.experience_run_js_setting
            ) {
              isMeterValue = true;
            }
          });
          let currentSegmentY = 100;

          let lastSegmentId = "";

          segments.forEach((segment: any, segindex: number) => {
            const segmentId = `segmentId_${segment.id}`;
            lastSegmentId = segmentId;
            sessionStorage.setItem(
              "pendingNodeAddition",
              JSON.stringify({
                experinceId: experienceId,
                segmentId: segmentId,
              })
            );

            const segmentNode = {
              id: segmentId,
              type: "selectorNode",
              position: { x: 300, y: currentSegmentY },
              draggable: false,
              data: {
                ...segment,
                experinceId: experienceId,
                label: "User Segment",
                type: "segment",
                id: segmentId,
                segmentId: segmentId,
                name: segment.segment_name,
                audienceType: segment.audience_type,
              },
            };
            newNodes.push(segmentNode);

            newEdges.push({
              id: `edge-${pageNode.id}-${segmentId}`,
              source: pageNode.id,
              target: segmentId,
              animated: false,
            });
            const lastSegmentY = currentSegmentY;
            const addButtonY = lastSegmentY + 150;

            const addPageNodeId = `addSegment_${pageNode.id}`;
            newNodes.push({
              id: addPageNodeId,
              type: "addButtonNode",
              position: { x: 300, y: addButtonY },
              draggable: false,
              className: "drop-id",
              data: {
                label: "+",
                onClick: () => {
                  navigate("/edit-user-segment");
                },
              },
            });
            newEdges.push({
              id: `edge-${pageNode.id}-${addPageNodeId}`,
              source: pageNode.id,
              target: addPageNodeId,
              type: "step",
              animated: false,
            });

            if (lastSegmentId) {
              newEdges.push({
                id: `edge-${addPageNodeId}-${lastSegmentId}`,
                source: addPageNodeId,
                target: lastSegmentId,
                type: "step",
                animated: false,
              });
            }

            const hasAnyChildConfig =
              (segment.viewport_exit_settings &&
                Object.keys(segment.viewport_exit_settings).length > 0) ||
              (segment.experience_cookie_setting &&
                segment.experience_cookie_setting.length > 0) ||
              (segment.experience_run_js_setting &&
                Object.keys(segment.experience_run_js_setting).length > 0) ||
              (segment.experience_template &&
                Object.keys(segment.experience_template).length > 0) ||
              (segment.pageview_meters &&
                Object.keys(segment.pageview_meters).length > 0) || 
                (segment.interactions &&
                  Object.keys(segment.interactions).length > 0);

            if (!hasAnyChildConfig) {
              const dropZoneId = `dropZone_${segmentId}`;
              const dropNode = {
                id: dropZoneId,
                type: "dropZone",
                style: {
                  Border: "1px solid red",
                },
                position: { x: 550, y: currentSegmentY },
                draggable: false,
                data: {
                  segmentId,
                  experinceId: experienceId,
                  label: "Drop Here",
                  type: "dropZone",
                },
                className: `drop-zone-${segmentId} drop_zone`,
              };
              newNodes.push(dropNode);

              newEdges.push({
                id: `edge-${segmentId}-${dropZoneId}`,
                source: segmentId,
                target: dropZoneId,
                animated: false,
                type: "smoothstep",
              });
            }
            currentSegmentY = currentSegmentY + (childrenCount + 1) * 200;

            var lastNodeId = segmentId;
            if (segment.viewport_exit_settings) {
              const viewPortData = segment.viewport_exit_settings;
              const viewportExitId = `viewportExitId_${viewPortData.id}`;
              const baseX = 600;
              const baseY = currentY + childrenCount * 120;

              newNodes.push({
                id: viewportExitId,
                type: "selectorNode",
                position: { x: baseX, y: baseY },
                draggable: false,
                data: {
                  ...viewPortData,
                  experinceId: experienceId,
                  segmentId,
                  label: "Viewport exit",
                  type: "viewExit",
                  title: viewPortData.title,
                  desktopSide: viewPortData.desktop_exit_side,
                  id: viewportExitId,
                },
              });

              newEdges.push({
                id: `edge-${segmentId}-${viewportExitId}`,
                source: segmentId,
                target: viewportExitId,
                animated: false,
              });

              let childX = baseX + 300;
              const childY = baseY;
              let lastNodeId = viewportExitId;

              // Run JS
              if (viewPortData.experience_run_js_setting) {
                const runjs = viewPortData.experience_run_js_setting;
                const runjsId = `runjsId_${runjs.id}`;

                newNodes.push({
                  id: runjsId,
                  type: "selectorNode",
                  position: { x: childX, y: childY },
                  draggable: false,
                  data: {
                    ...runjs,
                    experinceId: experienceId,
                    segmentId,
                    label: "Run JS",
                    type: "runjs",
                    title: runjs.title,
                    id: runjsId,
                  },
                });

                newEdges.push({
                  id: `edge-${viewportExitId}-${runjsId}`,
                  source: viewportExitId,
                  target: runjsId,
                  animated: false,
                });

                lastNodeId = runjsId;
                childX += 250;
              }

              if (viewPortData.experience_template) {
                const template = viewPortData.experience_template;
                const templateId = `showTemplateId_${template.id}`;

                newNodes.push({
                  id: templateId,
                  type: "selectorNode",
                  position: { x: childX, y: childY },
                  draggable: false,
                  data: {
                    ...template,
                    experinceId: experienceId,
                    segmentId,
                    label: "Show template",
                    type: "showTemplate",
                    delayType: template.delay_type,
                    displayType: template.display_type,
                    id: templateId,
                  },
                });

                newEdges.push({
                  id: `edge-${viewportExitId}-${templateId}`,
                  source: viewportExitId,
                  target: templateId,
                  animated: false,
                });

                lastNodeId = templateId;
                childX += 250;
              }

              // Cookies
              if (Array.isArray(viewPortData.experience_cookie_setting)) {
                viewPortData.experience_cookie_setting.forEach(
                  (cookie: any) => {
                    if (cookie.id) {
                      const cookieId = `cookieId_${cookie.id}`;

                      newNodes.push({
                        id: cookieId,
                        type: "selectorNode",
                        position: { x: childX, y: childY },
                        draggable: false,
                        data: {
                          ...cookie,
                          segmentId,
                          experinceId: experienceId,
                          label: "Set cookie",
                          type: "cookie",
                          cookiedetails: cookie.name,
                          title: cookie.title,
                          id: cookieId,
                        },
                      });

                      newEdges.push({
                        id: `edge-${viewportExitId}-${cookieId}`,
                        source: viewportExitId,
                        target: cookieId,
                        animated: false,
                      });

                      lastNodeId = cookieId;
                      childX += 250;
                    }
                  }
                );
              }

              const dropZoneId = `dropZone_${segmentId}`;
              newNodes.push({
                id: dropZoneId,
                type: "dropZone",
                position: { x: childX, y: childY },
                draggable: false,
                data: {
                  segmentId,
                  experinceId: experienceId,
                  label: "Drop Here",
                  type: "dropZone",
                },
                className: `drop-zone-${segmentId} drop_zone`,
              });

              newEdges.push({
                id: `edge-${lastNodeId}-${dropZoneId}`,
                source: lastNodeId,
                target: dropZoneId,
                animated: false,
                type: "smoothstep",
              });

              currentY = baseY + 150;
            } else if (!segment.viewport_exit_settings) {
              let previousNodeId = segmentId;
              let childIndex = 0;
              const startX = 600;
              const childSpacing = 250;
              const currentY = 100 + segindex * 200;
              if (segment?.interactions) {
                const intId = `intId_${segment.interactions.id}`;
                newNodes.push({
                  id: intId,
                  type: "selectorNode",
                  position: {
                    x: startX + childIndex * childSpacing,
                    y: currentY,
                  },
                  draggable: false,
                  data: {
                    ...segment.interactions,
                    experinceId: experienceId,
                    segmentId,
                    label: "Interaction",
                    type: "interaction",
                    intTitle: segment.interactions.title,
                    id: intId,
                  },
                });
                newEdges.push({
                  id: `edge-${previousNodeId}-${intId}`,
                  source: previousNodeId,
                  target: intId,
                  animated: false,
                });
                previousNodeId = intId;
                childIndex++;
              }
              if (Array.isArray(segment.experience_cookie_setting)) {
                segment.experience_cookie_setting.forEach((cookie: any) => {
                  if (cookie.id) {
                    const cookieId = `cookieId_${cookie.id}`;
                    newNodes.push({
                      id: cookieId,
                      type: "selectorNode",
                      position: {
                        x: startX + childIndex * childSpacing,
                        y: currentY,
                      },
                      draggable: false,
                      data: {
                        ...cookie,
                        segmentId,
                        experinceId: experienceId,
                        label: "Set cookie",
                        type: "cookie",
                        cookiedetails: cookie.name,
                        cookietitle: cookie.title,
                        id: cookieId,
                      },
                    });
                    newEdges.push({
                      id: `edge-${previousNodeId}-${cookieId}`,
                      source: previousNodeId,
                      target: cookieId,
                      animated: false,
                    });
                    previousNodeId = cookieId;
                    childIndex++;
                  }
                });
              }

              if (segment?.experience_run_js_setting) {
                const runjsId = `runjsId_${segment.experience_run_js_setting.id}`;
                newNodes.push({
                  id: runjsId,
                  type: "selectorNode",
                  position: {
                    x: startX + childIndex * childSpacing,
                    y: currentY,
                  },
                  draggable: false,
                  data: {
                    ...segment.experience_run_js_setting,
                    experinceId: experienceId,
                    segmentId,
                    label: "Run JS",
                    type: "runjs",
                    title: segment.experience_run_js_setting.title,
                    id: runjsId,
                  },
                });
                newEdges.push({
                  id: `edge-${previousNodeId}-${runjsId}`,
                  source: previousNodeId,
                  target: runjsId,
                  animated: false,
                });
                previousNodeId = runjsId;
                childIndex++;
              }

              if (segment?.experience_template) {
                const templateId = `showTemplateId_${segment.experience_template.id}`;
                newNodes.push({
                  id: templateId,
                  type: "selectorNode",
                  position: {
                    x: startX + childIndex * childSpacing,
                    y: currentY,
                  },
                  draggable: false,
                  data: {
                    ...segment.experience_template,
                    experinceId: experienceId,
                    segmentId,
                    label: "Show template",
                    type: "showTemplate",
                    delayType: segment.experience_template.delay_type,
                    displayType: segment.experience_template.display_type,
                    id: templateId,
                  },
                });
                newEdges.push({
                  id: `edge-${previousNodeId}-${templateId}`,
                  source: previousNodeId,
                  target: templateId,
                  animated: false,
                });
                previousNodeId = templateId;
                childIndex++;
              }
              if (
                (segment?.experience_template ||
                  segment?.experience_run_js_setting ||
                  segment.experience_cookie_setting) &&
                previousNodeId !== segmentId
              ) {
                const dropZoneId = `dropZone_${segmentId}`;
                newNodes.push({
                  id: dropZoneId,
                  type: "dropZone",
                  position: {
                    x: startX + childIndex * childSpacing,
                    y: currentY,
                  },
                  draggable: false,
                  data: {
                    segmentId,
                    experinceId: experienceId,
                    label: "Drop Here",
                    type: "dropZone",
                  },
                  className: `drop-zone-${segmentId} drop_zone`,
                });

                newEdges.push({
                  id: `edge-${previousNodeId}-${dropZoneId}`,
                  source: previousNodeId,
                  target: dropZoneId,
                  animated: false,
                  type: "smoothstep",
                });
                childrenCount++;
              }
            }

            let pageMeterId: any;
            if (segment.pageview_meters) {
              pageMeterId = `pageMeterId_${segment.pageview_meters.id}`;
              const baseX = 600;
              const baseY = currentY + childrenCount * 180;
              newNodes.push({
                id: pageMeterId,
                type: "selectorNode",
                position: { x: baseX, y: baseY },
                draggable: false,
                data: {
                  ...segment.pageview_meters,
                  experinceId: experienceId,
                  segmentId,
                  label: "Pageview Meter",
                  type: "pageMeter",
                  meterName: segment.pageview_meters.meter_name,
                  meterTitle: segment.pageview_meters.title,
                  id: pageMeterId,
                },
              });

              newEdges.push({
                id: `edge-${segmentId}-${pageMeterId}`,
                source: segmentId,
                target: pageMeterId,
                animated: false,
              });

              if (segment?.pageview_meters?.meter_values) {
                segment.pageview_meters.meter_values.forEach(
                  (meterValue: any, index: number) => {
                    const meterValueId = `${meterValue.id}`;
                    const meterY = currentY + childrenCount * 180;

                    newNodes.push({
                      id: meterValueId,
                      type: "selectorNode",
                      position: { x: 900, y: meterY },
                      draggable: false,
                      data: {
                        ...meterValue,
                        experinceId: experienceId,
                        segmentId,
                        label: "Meter Value",
                        type: "meterValue",
                        title: meterValue.title,
                        equalUnit: meterValue.equals_unit,
                        id: meterValueId,
                      },
                    });

                    newEdges.push({
                      id: `edge-${pageMeterId}-${meterValueId}`,
                      source: pageMeterId,
                      target: meterValueId,
                      animated: false,
                    });

                    childrenCount++;

                    const baseX = 1200;
                    let offsetX = 0;
                    const nodeSpacing = 250;

                    // Run JS
                    if (meterValue.experience_run_js_setting) {
                      const runjsId = `runjsId_${meterValue.experience_run_js_setting.id}`;
                      newNodes.push({
                        id: runjsId,
                        type: "selectorNode",
                        position: { x: baseX + offsetX, y: meterY },
                        draggable: false,
                        data: {
                          ...meterValue.experience_run_js_setting,
                          experinceId: experienceId,
                          segmentId,
                          label: "Run JS",
                          type: "runjs",
                          title: meterValue.experience_run_js_setting.title,
                          id: runjsId,
                        },
                      });
                      newEdges.push({
                        id: `edge-${meterValueId}-${runjsId}`,
                        source: meterValueId,
                        target: runjsId,
                        animated: false,
                        type: "simplebezier",
                      });
                      offsetX += nodeSpacing;
                    }

                    // Set cookie
                    if (Array.isArray(meterValue.experience_cookie_setting)) {
                      meterValue.experience_cookie_setting.forEach(
                        (cookie: any, i: number) => {
                          const cookieId = `cookieId_${cookie.id}`;
                          newNodes.push({
                            id: cookieId,
                            type: "selectorNode",
                            position: { x: baseX + offsetX, y: meterY },
                            draggable: false,
                            data: {
                              ...cookie,
                              segmentId,
                              experinceId: experienceId,
                              label: "Set cookie",
                              type: "cookie",
                              cookiedetails: cookie.name,
                              title: cookie.title,
                              id: cookieId,
                            },
                          });
                          newEdges.push({
                            id: `edge-${meterValueId}-${cookieId}`,
                            source: meterValueId,
                            target: cookieId,
                            type: "simplebezier",
                            animated: false,
                          });
                          offsetX += nodeSpacing;
                        }
                      );
                    }

                    // Show template
                    if (meterValue.experience_template) {
                      const templateId = `showTemplateId_${meterValue.experience_template.id}`;
                      newNodes.push({
                        id: templateId,
                        type: "selectorNode",
                        position: { x: baseX + offsetX, y: meterY },
                        draggable: false,
                        data: {
                          ...meterValue.experience_template,
                          experinceId: experienceId,
                          segmentId,
                          label: "Show template",
                          type: "showTemplate",
                          delayType: meterValue.experience_template.delay_type,
                          displayType:
                            meterValue.experience_template.display_type,
                          id: templateId,
                        },
                      });
                      newEdges.push({
                        id: `edge-${meterValueId}-${templateId}`,
                        source: meterValueId,
                        target: templateId,
                        type: "default",
                        animated: false,
                      });
                      offsetX += nodeSpacing;
                    }

                    const dropZoneId = `dropZone_${segmentId}_${meterValueId}`;
                    newNodes.push({
                      id: dropZoneId,
                      type: "dropZone",
                      position: { x: baseX + offsetX, y: meterY },
                      draggable: false,
                      data: {
                        meterValueId,
                        segmentId,
                        experinceId: experienceId,
                        label: "Drop Here",
                        type: "dropZone",
                      },
                      className: `drop-zone-${meterValueId} drop_zone`,
                    });

                    newEdges.push({
                      id: `edge-${meterValueId}-${dropZoneId}`,
                      source: meterValueId,
                      target: dropZoneId,
                      animated: false,
                    });
                  }
                );
                const lastMeterY = currentY + childrenCount * 180;

                const addMeterValueNodeId = `addMeterValue_${segment?.pageview_meters?.id}`;
                const addButtonY = lastMeterY + 120;

                newNodes.push({
                  id: addMeterValueNodeId,
                  type: "addButtonNode",
                  position: { x: 900, y: addButtonY },
                  draggable: false,
                  data: {
                    label: "+",
                    onClick: () => {
                      navigate("/edit-page-value", {
                        state: {
                          meterId: segment.pageview_meters.id,
                          experienceId: segment.experinceId,
                          segmentId: `segment_${segment.id}`,
                        },
                      });
                    },
                  },
                });

                newEdges.push({
                  id: `edge-${pageMeterId}-${addMeterValueNodeId}`,
                  source: pageMeterId,
                  target: addMeterValueNodeId,
                  type: "smoothstep",
                  animated: false,
                });
              }
            }

            if (!isMeterValue) {
              const dropZoneId = `dropZone_${segmentId}`;
              newNodes.push({
                id: dropZoneId,
                type: "dropZone",
                position: { x: 600, y: currentY + (childrenCount + 1) * 150 },
                draggable: false,
                data: {
                  segmentId,
                  experinceId: experienceId,
                  label: "Drop Here",
                  type: "dropZone",
                },
                className: `drop-zone-${segmentId} drop_zone`,
              });
              newEdges.push({
                id: `edge-${lastNodeId}-${dropZoneId}`,
                source: lastNodeId,
                target: dropZoneId,
                animated: false,
              });
            }
          });
        } else {
          const defaultSegmentId = `segmentId_default`;
          const defaultSegmentNode = {
            id: defaultSegmentId,
            type: "selectorNode",
            position: { x: 300, y: 100 },
            draggable: false,
            data: {
              label: "Segment",
              type: "segment",
              id: defaultSegmentId,
              experinceId: experienceId,
              name: "Segment",
            },
          };
          newNodes.push(defaultSegmentNode);
          newEdges.push({
            id: `edge-${pageNode.id}-${defaultSegmentId}`,
            source: pageNode.id,
            target: defaultSegmentId,
            animated: false,
          });
        }

        setNodes(newNodes);
        setEdges(newEdges);
      } catch (error) {
        console.error("Error fetching experience data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchExperience();
  }, [experienceId]);

  const [formData, setFormData] = useState({
    name: "",
    type: "",
    description: "",
  });

  const handleTypeChange = (e: any) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  const handleUpdate = async () => {
    const newErrors = {
      title: !formData.name,
      useCase: !formData.type,
    };
    if (!formData.name.trim()) {
      setErrors({ name: true });
      return;
    }
    setErrors(newErrors);
    try {
      setLoading(true);
      const response = await EditExperienceList(
        experienceId,
        formData.name,
        formData.type,
        formData.description
      );
      if (response.success === true) {
        toast.success(response.message);
        navigate("/experience/list");
      } else {
        toast.error(response.error);
      }
    } catch (error) {
      console.error("Error fetching experience:", error);
    }
    setLoading(false);
  };
  const handleCancel = () => {
    navigate("/experience/list");
  };
  const onback = () => {
    navigate("/experience/list");
  };

  const onDrop = useCallback(
    (event: any) => {
      event.preventDefault();
      const isNavigation =
        event.dataTransfer.getData("is-navigation") === "true";
      const path = event.dataTransfer.getData("text/plain");
      const experinceId = event.dataTransfer.getData("experinceId");

      const id = event.target.dataset.id;
      if (!id) return;

      const parts = id.split("_");
      const segId = parts[2];
      const meterValueId = parts[3];
      const nodeType = event.dataTransfer.getData("application/reactflow");
      const label = event.dataTransfer.getData("label") || `${nodeType} node`;
      const isViewportNode =
        nodeType === "selectorNode" || label === "Viewport Exit";
      const isRunjsNode = nodeType === "selectorNode" || label === "Run JS";
      const isTemplateNode =
        nodeType === "selectorNode" || label === "Show Template";
      const isPageMeterNode =
        nodeType === "selectorNode" || label === "Pageview Meter";
      const isSetCookieNode =
        nodeType === "selectorNode" || label === "Set Cookies";
        const isInteractionNode =
        nodeType === "selectorNode" || label === "Interaction";
      const currentNodes = reactFlowInstance?.getNodes?.() || [];

      const storedNodeType = sessionStorage.getItem("nodeType");
      const isPaywall = storedNodeType === "paywall";

      const hasPageMeterNode = nodes.some(
        (node: any) => node.data.type === "pageMeter"
      );

      if (!isPaywall) {
        if (isPageMeterNode && !hasPageMeterNode) {
          toast.error(" Page Meter node is allowed in paywall mode.");
          return;
        }
      }
      if (
        isPaywall &&
        (isTemplateNode || isRunjsNode || isSetCookieNode) &&
        !hasPageMeterNode
      ) {
        toast.error(
          "Please add a PageView Meter node before adding other nodes."
        );
        return;
      }
      if (isViewportNode) {
        const viewportExists = currentNodes.some(
          (node: any) =>
            node.data?.type === "viewExit" &&
            node.data?.user_segment_id === Number(segId)
        );
        const interactionExists = currentNodes.some(
          (node: any) =>
            node.data?.type === "interaction" &&
            node.data?.user_segment_id === Number(segId)
        );
        if (viewportExists) {
          toast.error("Viewport Exit node already exists for this segment.");
          return;
        }
        if (interactionExists) {
          toast.error("Viewport Exit node is not allowed for this segment.");
          return;
        }
        if (isPaywall && isViewportNode) {
          toast.error("Viewport Exit node is not allowed for paywall.");
          return;
        }
        const runjsExists = currentNodes.some(
          (node: any) =>
            node.data?.type === "runjs" &&
            node.data?.user_segment_id === Number(segId)
        );

        const templateExists = currentNodes.some(
          (node: any) =>
            node.data?.type === "showTemplate" &&
            node.data?.user_segment_id === Number(segId)
        );
        const setCookieExists = currentNodes.some(
          (node: any) =>
            node.data?.type === "cookie" &&
            node.data?.user_segment_id === Number(segId)
        );
        if (runjsExists || templateExists || setCookieExists) {
          toast.error("Cannot drop ViewPort Exit node for this segment.");
          return;
        }
      }

      if (isPageMeterNode) {
        const pageMeterExists = currentNodes.some(
          (node: any) =>
            node.data?.type === "pageMeter" &&
            node.data?.user_segment_id === Number(segId)
        );
        if (pageMeterExists) {
          toast.error("Page View Meter node already exists for this segment.");
          return;
        }
      }

      if (isTemplateNode) {
        const templateExists = currentNodes.some((node: any) => {
          const isSameSegment = node.data?.user_segment_id === Number(segId);
          const isSameType = node.data?.type === "showTemplate";
          if (meterValueId) {
            return (
              isSameType &&
              isSameSegment &&
              node.data?.meter_value_id === Number(meterValueId)
            );
          }
          return isSameType && isSameSegment;
        });

        if (templateExists) {
          toast.error("Template node already exists for this segment.");
          return;
        }
      }

      if (isRunjsNode) {
        const runjsExists = currentNodes.some((node: any) => {
          const isSameSegment = node.data?.user_segment_id === Number(segId);
          const isSameType = node.data?.type === "runjs";
          if (meterValueId) {
            return (
              isSameType &&
              isSameSegment &&
              node.data?.meter_value_id === Number(meterValueId)
            );
          }
          return isSameType && isSameSegment;
        });

        if (runjsExists) {
          toast.error("RunJS node already exists for this segment.");
          return;
        }
      }
      
      if (isInteractionNode) {
        const interactionExists = currentNodes.some(
          (node: any) =>
            node.data?.type === "interaction" &&
            node.data?.user_segment_id === Number(segId)
        );
        const viewportExists = currentNodes.some(
          (node: any) =>
            node.data?.type === "viewExit" &&
            node.data?.user_segment_id === Number(segId)
        );
        if (viewportExists) {
          toast.error("Cannot add Interaction node when Viewport Exit node exists.");
          return;
        }
        if (interactionExists) {
          toast.error("Interaction node already exists for this segment.");
          return;
        }
        if (isPaywall && isInteractionNode) {
          toast.error("Interaction node is not allowed for paywall.");
          return;
        }
      }


      sessionStorage.setItem(
        "pendingNodeAddition",
        JSON.stringify({
          experinceId: experienceId,
          segmentId: segId,
          meterValueId: meterValueId,
        })
      );

      if (isNavigation && path) {
        navigate(path);
        return;
      }

      if (!reactFlowInstance || !flowWrapperRef.current) return;
      if (!nodeType) return;

      const flowBounds = flowWrapperRef.current.getBoundingClientRect();
      const x = event.clientX - flowBounds.left;
      const y = event.clientY - flowBounds.top;

      try {
        const position = reactFlowInstance.project({ x, y });

        const elements = document.elementsFromPoint(
          event.clientX,
          event.clientY
        );
        const dropZoneEl = elements.find((el) =>
          el.classList.contains("drop-zone")
        );

        if (!dropZoneEl) {
          console.warn("Drop zone not found under drop point.");
          return;
        }

        const dropZoneId = dropZoneEl.getAttribute("data-node-id");
        if (!dropZoneId || !segId) {
          console.warn("Drop zone does not have proper attributes.");
          return;
        }

        const newNode = {
          id: segId,
          type: nodeType,
          position,
          data: {
            label,
            type: nodeType,
            segmentId: segId,
            experinceId,
          },
        };

        setNodes((nds) => [...nds, newNode]);
        setEdges((eds) => [
          ...eds,
          {
            id: `edge-${dropZoneId}-${segId}`,
            source: dropZoneId,
            target: segId,
            animated: false,
          },
        ]);
      } catch (error) {
        console.error("Could not calculate position:", error);
      }
    },
    [reactFlowInstance, setNodes, setEdges, navigate]
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);
  return (
    <>
      <div className="experience-container">
        <div className="experience-container__header">
          <div className="d-center">
            <span className="Icon">
              <IconButton aria-label="close" onClick={onback}>
                <ArrowbackIcon />
              </IconButton>{" "}
            </span>{" "}
            <h2 className="mb0"> Experience - {name}</h2>
          </div>
          <Tabs
            className="experience-container__tabination"
            value={activeTab}
            onChange={handleChange}
            aria-label="Settings and Edit Tabs"
          >
            <Tab
              icon={
                <Tooltip title="Setting" placement="top" arrow>
                  <SettingIcon />
                </Tooltip>
              }
              className="experience-container__tabination--tabs"
              sx={{
                borderRight: "1px solid rgba(220, 220, 220, 1)",
              }}
            />
            <Tab
              className="experience-container__tabination--tabs"
              icon={
                <Tooltip title="Edit Experience" placement="top" arrow>
                  <EditIconIcon />
                </Tooltip>
              }
            />
          </Tabs>
        </div>

        {/* Settings Tab */}
        {activeTab === 0 && (
          <Box mt={2} className="experience-container__section">
            <div className="experience-container__section--heading">
              <div className="d-center">
                <span className="Icon">
                  <ExperienceIcon />
                </span>{" "}
                <h2>Experience</h2>
              </div>
            </div>

            <div className="experience-container__section--content ">
              <Grid
                container
                spacing={{ xs: 0, sm: 2, md: 2 }}
                sx={{ borderBottom: "1px solid rgba(212, 212, 212, 1)" }}
              >
                <Grid
                  size={{ xs: 12, md: 3, sm: 6 }}
                   className="editModal_form"
                >
                  <Typography fontWeight="bold" color="red">
                    Name:
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 9, sm: 6 }} sx={{ padding: "1rem" }}>
                  <TextField
                    required
                    variant="standard"
                    fullWidth
                    name="name"
                    value={formData.name}
                    error={errors.name}
                    helperText={errors.name ? "Name is required" : ""}
                    onChange={(e) => {
                      const { name, value } = e.target;

                      setFormData((prev) => ({
                        ...prev,
                        [name]: value,
                      }));

                      setErrors((prev: any) => ({
                        ...prev,
                        [name]: value.trim() === "",
                      }));
                    }}
                  />
                </Grid>
              </Grid>

              <Grid
                container
                spacing={{ xs: 0, sm: 2, md: 2 }}
                sx={{ borderBottom: "1px solid rgba(212, 212, 212, 1)" }}
              >
                <Grid
                  size={{ xs: 12, md: 3, sm: 6 }}
                   className="editModal_form"
                >
                  <Typography fontWeight="bold" color="red">
                    Type:
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 9, sm: 6 }} sx={{ padding: "1rem" }}>
                  <TextField
                    select
                    fullWidth
                    required
                    variant="standard"
                    name="type"
                    value={formData.type}
                    onChange={handleTypeChange}
                    error={errors.type}
                    helperText={errors.type ? "Type is required" : ""}
                  >
                    <MenuItem value={formData.type}></MenuItem>
                    {["paywall", "lightbox"].map((option) => (
                      <MenuItem key={option} value={option}>
                        {option.charAt(0).toUpperCase() + option.slice(1)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>

              <Grid
                container
                spacing={{ xs: 0, sm: 2, md: 2 }}
                sx={{ borderBottom: "1px solid rgba(212, 212, 212, 1)" }}
              >
                <Grid
                  size={{ xs: 12, md: 3, sm: 6 }}
                   className="editModal_form"
                >
                  <Typography fontWeight="bold" color="red">
                    Description:
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 9, sm: 6 }} sx={{ padding: "1rem" }}>
                  <TextField
                    variant="standard"
                    fullWidth
                    name="description"
                    multiline
                    rows={4}
                    value={formData.description}
                    onChange={handleTypeChange}
                  />
                </Grid>
              </Grid>
            </div>
            <Box
              m={2}
              mb={3}
              display={"flex"}
              justifyContent={"end"}
              className="modal_footer--button"
            >
              <Button
                sx={{
                  "--variant-outlinedBorder": "rgba(228, 0, 43, 1)",
                  "--variant-outlinedColor": "rgba(228, 0, 43, 1)",
                  "--variant-containedBg": "rgba(228, 0, 43, 1)",

                  "&:hover": {
                    "--variant-outlinedBorder": "rgba(228, 0, 43, 1)",
                    "--variant-outlinedBg": "rgba(228, 0, 43, 0.04)",
                    "--variant-containedBg": "rgba(228, 0, 43, 1)",
                    "--variant-textBg": "rgba(228, 0, 43, 0.04)",
                  },
                }}
                color="primary"
                className="button"
                variant="outlined"
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                className="button"
                sx={{
                  "--variant-containedBg": "rgba(228, 0, 43, 1)",
                  "&:hover": {
                    "--variant-containedBg": "rgba(228, 0, 43, 1)",
                    "--variant-textBg": "rgba(228, 0, 43, 0.04)",
                  },
                }}
                variant="contained"
                color="error"
                type="submit"
                onClick={handleUpdate}
                disabled={loading}
              >
                {loading ? <span className="loader" /> : "Update"}
              </Button>

              <ToastContainer position="top-right" autoClose={3000} />
            </Box>
          </Box>
        )}

        {/* Edit Tab */}
        {activeTab === 1 && (
          <div
            ref={flowWrapperRef}
            style={{
              position: "relative",
              width: "100%",
              height: "100vh",
            }}
          >
            {isloading ? (
              <div className="loader-overlay">
                <div className="loader-spinner"></div>
                <p>Loading experience data...</p>
              </div>
            ) : (
              <div
                style={{
                  background: "#ffffff",
                  border: "1px solid rgb(220, 220, 220)",
                  height: "100vh",
                  marginTop: "1rem",
                }}
              >
                <Grid
                  container
                  className="templates__header"
                  sx={{
                    borderBottom: "1px solid rgb(220, 220, 220)",
                  }}
                >
                  <Grid
                    size={{ xs: 12, md: 4, sm: 6 }}
                    display={"flex"}
                    justifyContent={"center"}
                    alignItems={"center"}
                    className="templates__header--search"
                  >
                    <span className="Icon">
                      <TouchpointIcon />
                    </span>{" "}
                    &nbsp; TouchPoints
                  </Grid>

                  <Grid
                    size={{ xs: 12, md: 8, sm: 6 }}
                    display={"flex"}
                    justifyContent={"center"}
                    alignItems={"center"}
                    className="templates__header--search"
                  >
                    Experience Logic
                  </Grid>
                </Grid>
                <ReactFlowProvider>
                  <ReactFlow
                    style={{
                      overflow: "auto",
                    }}
                    nodes={nodes}
                    edges={edges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    onConnect={onConnect}
                    onInit={(instance) => setReactFlowInstance(instance)}
                    onDrop={onDrop}
                    onDragOver={onDragOver}
                    nodeTypes={nodeTypes}
                    snapGrid={snapGrid}
                    defaultViewport={defaultViewport}
                    defaultEdgeOptions={{ type: "step" }}
                    panOnDrag={true}
                    zoomOnScroll={false}
                    zoomOnPinch={false}
                    zoomOnDoubleClick={false}
                    panOnScroll={true}
                  ></ReactFlow>
                </ReactFlowProvider>
                <ToastContainer position="top-right" autoClose={3000} />
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default EditExperience;
